import type { CouponDetail, CouponItem } from './types'

/**
 * Coupon response readers, kept apart from `api.ts` because they are pure
 * functions over a payload and nothing here should need the HTTP client. That
 * also lets them be unit-tested directly: the Cypress preprocessor does not
 * resolve the `@/` alias, so anything reachable from a spec has to stay clear
 * of value imports that use it.
 */

/**
 * A code the server recognised. Any coupon at all counts, expanded or not, but
 * an expanded one the server marked `valid: false` is a dead coupon: accepting
 * it promises a discount the subscribe call will not honour.
 */
export function findCoupon(coupons: CouponItem[], code: string): CouponItem | undefined {
  return coupons.find((item) => {
    if (item.code !== code || !item.coupon) return false
    return typeof item.coupon === 'string' || item.coupon.valid !== false
  })
}

/**
 * The coupon's terms, or null when the server sent only its id. A bare id still
 * means the code is good — it just cannot say what the code discounts, so callers
 * fall back to letting Stripe apply it at billing.
 *
 * Both wire spellings are read here so no caller has to know which it got. The
 * endpoint answers camelCase while Stripe's own field names are snake_case, and
 * reading one spelling yields undefined for every amount — which values a
 * 100%-off coupon at nothing and bills the customer in full.
 */
export function couponDetail(item: CouponItem | undefined): CouponDetail | null {
  const wire = typeof item?.coupon === 'object' && item.coupon !== null ? item.coupon : null
  if (!wire) return null

  return {
    duration: wire.duration,
    duration_in_months: wire.duration_in_months ?? wire.durationInMonths,
    percent_off: wire.percent_off ?? wire.percentOff,
    amount_off: wire.amount_off ?? wire.amountOff,
  }
}
